import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import './OnlineSession.css';

function FaceToFaceSession() {
  const { courseId, duration } = useParams();
  const numericDuration = parseInt(duration, 10);
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingRequest, setPendingRequest] = useState(null);
  const [declinedRequest, setDeclinedRequest] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const auth = getAuth();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) {
      setError('You must be signed in to access this page.');
      setLoading(false);
    }
    if (!courseId) {
      setError('Invalid course ID.');
      setLoading(false);
    }
  }, [currentUser, courseId]);

  const fetchTutors = useCallback(async () => {
    if (!currentUser || !courseId) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/face-to-face/tutors/nearby?courseId=${courseId}`;
      console.log('Fetching tutors from:', apiUrl, 'for user:', currentUser.uid);
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      console.log('API response:', response.data.map(t => ({ uid: t.uid, distance: t.distance })));
      setTutors(response.data);
      if (response.data.length === 0) {
        setError('No tutors available within 5000km for this course.');
      }
    } catch (err) {
      console.error('Error fetching tutors:', {
        message: err.message,
        code: err.code,
        response: err.response ? { status: err.response.status, data: err.response.data } : null,
      });
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your network and try again.');
      } else if (err.response?.status === 400) {
        setError('Please allow location access so we can find nearby tutors.');

        // Only attempt geolocation if the browser supports it
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              try {
                const token = await auth.currentUser.getIdToken();

                // Save the coordinates to the backend
                await axios.post(
                  `${process.env.REACT_APP_API_BASE_URL}/user/${auth.currentUser.uid}/update-location`,
                  {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  },
                  { headers: { Authorization: `Bearer ${token}` } }
                );

                // Clear previous error and retry fetching tutors
                setError('');
                fetchTutors(); // Retry fetching tutors after saving location
              } catch (updateErr) {
                console.error('Error saving location:', updateErr);
                setError('Failed to save your location. Please try again.');
              }
            },
            (geoErr) => {
              console.error('Geolocation error:', geoErr);

              if (geoErr.code === geoErr.PERMISSION_DENIED) {
                setError(
                  'Location access denied. Please enable location services in your browser to find nearby tutors.'
                );
              } else {
                setError(
                  'We could not access your location. Please make sure location services are enabled.'
                );
              }

              setShowLocationPrompt(true); // fallback: show manual location button
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } else {
          setError('Geolocation is not supported by your browser.');
          setShowLocationPrompt(true); // fallback
        }
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please sign in again.');
      } else if (err.response?.status === 404) {
        setError('No tutors available for this course.');
      } else {
        setError(`Failed to fetch tutors: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, currentUser, auth]);

  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);

  const handleRequestSession = async (tutor) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/${auth.currentUser.uid}/data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userPoints = userRes.data.points ?? 0;

      // Correct session price calculation (rounded to 2 decimal places)
      const sessionPrice = Number(((numericDuration / 60) * tutor.faceToFacePrice).toFixed(2));

      if (userPoints < sessionPrice) {
        alert(`You don't have enough points. This session costs R${sessionPrice}, but you only have R${userPoints}.`);
        return;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/face-to-face/session-requests`,
        {
          tutorId: tutor.uid,
          courseId,
          duration: numericDuration,
          price: sessionPrice,
          type: 'face-to-face',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPendingRequest({ tutorId: tutor.uid, requestId: res.data.requestId, expiresAt: Date.now() + 30000 });

      // ✅ Send push notification to tutor (same as online session)
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/notify-tutor`,
        {
          tutorId: tutor.uid,
          sessionId: res.data.requestId,
          courseName: tutor.courseName || 'Course',
          studentName: auth.currentUser.displayName || 'Student',
          type: 'face-to-face'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/session-requests/${res.data.requestId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (statusRes.data.status === 'accepted') {
            clearInterval(pollInterval);
            const sessionId = statusRes.data.sessionId;
            navigate(`/dashboard/tracking/${sessionId}`, {
              state: {
                role: 'student',
                studentId: auth.currentUser.uid,
                tutorId: tutor.uid
              }
            });
          } else if (statusRes.data.status === 'declined') {
            clearInterval(pollInterval);
            setDeclinedRequest(tutor.uid);
            setPendingRequest(null);
            setTimeout(() => setDeclinedRequest(null), 3000);
          }
        } catch (err) {
          console.error('Error checking session status:', err);
        }
      }, 2000);

      setTimeout(() => clearInterval(pollInterval), 30000);
    } catch (err) {
      console.error('Error requesting session:', err);
      alert(err.response?.data?.error || 'Failed to request session');
    }
  };

  useEffect(() => {
    if (pendingRequest) {
      const timer = setTimeout(() => setPendingRequest(null), 30000);
      return () => clearTimeout(timer);
    }
  }, [pendingRequest]);

  const handleSetLocation = () => {
    navigate('/profile/edit');
  };

  if (loading) return <div>Loading tutors...</div>;
  if (error) return (
    <div style={{ color: 'red' }}>
      {error}
      {showLocationPrompt && (
        <div>
          <button onClick={handleSetLocation}>Set Your Location</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="online-session-container">
      <h2>Face-to-Face Tutors</h2>
      {pendingRequest && (
        <div className="pending-request-banner">
          <p>
            Waiting for tutor response... You can request another tutor in{' '}
            {Math.ceil((pendingRequest.expiresAt - Date.now()) / 1000)} seconds
          </p>
        </div>
      )}
      {declinedRequest && (
        <div className="declined-request-banner">
          <p>Your session request was declined by the tutor.</p>
        </div>
      )}
      {tutors.length === 0 ? (
        <p>No tutors available within 5000km for this course.</p>
      ) : (
        <div>
          <h3>Currently available tutors nearby:</h3>
          <ul className="tutor-list">
            {tutors.map((tutor, index) => {
              const calculatedPrice = ((numericDuration / 60) * tutor.faceToFacePrice).toFixed(2);
              return (
                <li key={index} className="tutor-item">
                  <div className="tutor-info-container">
                    <div className="tutor-details">
                      <p><strong>{tutor.username}</strong></p>
                      <p>Price for this session: R{calculatedPrice}</p>
                      <p>Distance: {tutor.distance ? tutor.distance.toFixed(2) : 'Unknown'} km</p>
                    </div>
                    <div className="tutor-profile-pic-container">
                      <img src={tutor.profilePictureUrl || '/default-profile.png'} alt={`${tutor.username} profile`} className="tutor-profile-pic" />
                    </div>
                  </div>
                  <button onClick={() => handleRequestSession(tutor)} disabled={pendingRequest !== null && pendingRequest.tutorId === tutor.uid}>
                    {pendingRequest && pendingRequest.tutorId === tutor.uid ? 'Request Pending...' : 'Request Session'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FaceToFaceSession;
